import {ISSUE_VIEWER_CACHE_TTL_MS} from '@github-ui/preheat-issues-manager/issue-viewer-storage-key'
import {queryOptions} from '@github-ui/react-query'
import type {createServerPersistedQueryPersister} from '@github-ui/react-query-persister'
import {IssueParticipants} from '@github-ui/server-persisted-queries'
import type {IssueParticipantsQuery} from '@github-ui/server-persisted-queries/types/IssueParticipants'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

type ParticipantsConnection = NonNullable<
  NonNullable<NonNullable<IssueParticipantsQuery['repository']>['issue']>['participants']
>

export type ParticipantNode = NonNullable<NonNullable<ParticipantsConnection['nodes']>[number]>

export function participantsQueryKey(owner: string, repo: string, number: number) {
  return ['issue-viewer', 'participants', {owner, repo, number}] as const
}

// Built at module scope so the cached `queryFn` retains only these arguments, not a component's closure scope.
export function participantsQueryOptions(
  owner: string,
  repo: string,
  number: number,
  persister: ReturnType<typeof createServerPersistedQueryPersister>,
) {
  return queryOptions({
    queryKey: participantsQueryKey(owner, repo, number),
    queryFn: ({signal}) => fetchParticipants(owner, repo, number, signal),
    gcTime: ISSUE_VIEWER_CACHE_TTL_MS,
    persister,
  })
}

export async function fetchParticipants(
  owner: string,
  repo: string,
  number: number,
  signal?: AbortSignal,
): Promise<ParticipantNode[]> {
  const response = await verifiedFetchJSON(
    `/${owner}/${repo}/issues/${number}/participants?queryId=${IssueParticipants}`,
    {signal},
  )
  if (!response.ok) {
    throw new Error(`Participants fetch failed (${response.status})`)
  }

  const json = (await response.json()) as {data?: IssueParticipantsQuery; errors?: unknown[]}
  if (!json.data) {
    throw new Error('Participants query returned no data')
  }

  return (json.data.repository?.issue?.participants?.nodes || []).flatMap(node => (node ? node : []))
}
